import { GameIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useRef, useState } from "react";
import { RINGS, STAMINA_COST, SHIELDS } from "@zend/content";
import { marchMs, tileDistance } from "@zend/rules";
import { pick, useT } from "../i18n/index.js";
import { Sheet } from "../base/panels/shared.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { Timer, formatDuration, useServerTick } from "../ui/Timer.js";
import { useSession } from "../state/session.js";
import { useBalances } from "../state/balances.js";
import { useUnits } from "../units/state.js";
import { toast } from "../ui/Toast.js";
import { SquadPreview } from "./SquadPreview.js";
import { TargetIntel } from "./TargetIntel.js";
import { worldApi } from "./api.js";
import { socialApi } from "../social/api.js";
import { useWorld } from "./state.js";
import type { WorldTile } from "./types.js";
import "./revenge.css";

export function kindName(kind: string, t: ReturnType<typeof useT>): string {
  const key = `world.kind.${kind}`;
  const text = t(key);
  return text === key ? kind : text;
}

/** Bottom sheet for a selected tile: what it is, how far, what it costs, and the actions (R41–R46). */
export function TileSheet() {
  const t = useT();
  const now = useServerTick(), revenge = useWorld(s => s.revenge), actionLock = useRef(false);
  const sel = useWorld((s) => s.selection);
  const me = useWorld((s) => s.me);
  const select = useWorld((s) => s.select);
  const march = useWorld((s) => s.march);
  const scout = useWorld((s) => s.scout);
  const run = useWorld((s) => s.run);
  const squadSlot = useWorld((s) => s.squadSlot);
  const setSquadSlot = useWorld((s) => s.setSquadSlot);
  const camp = useWorld((s) => s.camp);
  const refreshCamp = useWorld((s) => s.refreshCamp);
  const setJump = useWorld((s) => s.setJump);
  const account = useSession((s) => s.account);
  const balances = useBalances((s) => s.balances);
  const roster = useUnits((s) => s.roster);
  const refreshRoster = useUnits((s) => s.refreshRoster);
  const [busy, setBusy] = useState(false);
  const [watchBattle, setWatchBattle] = useState(true);

  useEffect(() => {
    if (!roster) void refreshRoster();
  }, [roster, refreshRoster]);
  useEffect(() => {
    if (sel?.tile?.kind === "camp") void refreshCamp(sel.x, sel.y);
  }, [sel, refreshCamp]);

  if (!sel || !me) return null;
  const tile: WorldTile | null = sel.tile;
  const own = tile?.owner?.id === account?.id;
  const distance = tileDistance(me.position.x, me.position.y, sel.x, sel.y);
  const eta = marchMs(distance, me.marchSpeedPct);
  const ring = RINGS.find((r) => r.id === (tile?.ring ?? 1));
  const staminaFor = (kind: string) => STAMINA_COST[`attack:${kind}`] ?? 0;
  const squads = roster?.squads ?? [];
  const chosen = squads.find((s) => s.slot === squadSlot);
  const marchSlotsFull = me.marches.filter((entry) => entry.action !== "scout").length >= me.marchSlots;
  const title = tile
    ? `${kindName(tile.kind, t)}${tile.level > 0 && tile.kind !== "enclave" ? ` L${tile.level}` : ""}`
    : t("world.kind.empty");

  const act = async (fn: () => Promise<unknown>) => {
    if (actionLock.current) return;
    actionLock.current = true;
    setBusy(true);
    try {
      await fn();
    } finally {
      actionLock.current = false;
      setBusy(false);
    }
  };
  const share = async () => {
    try {
      await navigator.clipboard.writeText(`${sel.x},${sel.y}`);
      toast.success(t("world.toast.copied"));
    } catch {
      toast.info(`${sel.x},${sel.y}`);
    }
  };
  const bookmark = () =>
    act(() =>
      run(
        () => worldApi.addBookmark(tile ? kindName(tile.kind, t) : "?", sel.x, sel.y, tile?.kind ?? "pin"),
        t("world.toast.bookmarked"),
      ),
    );

  const squadPicker =
    squads.length > 0 && !own && tile && tile.kind !== "camp" && tile.kind !== "citadel" ? (
      <div data-testid="squad-picker">
        <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {squads.map((s) => (
            <button
              key={s.slot}
              type="button"
              className={`tab ${s.slot === squadSlot ? "active" : ""}`}
              onClick={() => setSquadSlot(s.slot)}
              disabled={!s.ready}
              data-testid={`pick-squad-${s.slot}`}
            >
              {s.name || t("world.squad", { n: s.slot })} · <StatIcon stat="power" />
              {formatCompact(s.power)}
              {!s.ready ? <FunctionalIcon name="lock" /> : null}
            </button>
          ))}
        </div>
        {chosen && roster ? (
          <SquadPreview
            squad={{
              name: chosen.name,
              members: Object.entries(chosen.members).flatMap(([slot, id]) => {
                const unit = roster.units.find((row) => row.id === id);
                return unit
                  ? [
                      {
                        rowId: unit.id,
                        unitId: unit.unitId,
                        slot,
                        level: unit.level,
                        stars: unit.stars,
                        skin: unit.skin,
                      },
                    ]
                  : [];
              }),
            }}
          />
        ) : null}
        <p className="hint" data-testid="march-slot-stock">
          {t("world.stamina.slots", {
            used: me.marches.filter((march) => march.action !== "scout").length,
            cap: me.marchSlots,
          })}
        </p>
      </div>
    ) : null;

  const attackButton = (kind: string, testId: string) => (
    <Button
      variant="primary"
      disabled={busy || marchSlotsFull || !chosen?.ready || me.stamina.current < staminaFor(kind) || Boolean(kind === "enclave" && tile?.owner?.shielded) || Boolean(revenge && now + eta >= revenge.expiresAt)}
      onClick={() =>
        void act(async () => {
          const next = await march({ x: sel.x, y: sel.y, action: "attack" });
          if (next) {
            useWorld.setState({ watchedMarch: watchBattle ? next.id : null });
            useWorld.getState().selectMarch(next.id);
          }
        })
      }
      data-testid={testId}
    >
      <FunctionalIcon name="attack" /> {t(revenge ? "world.revengeStart" : kind === "enclave" ? "world.raid" : "world.attack")} · {staminaFor(kind)} <StatIcon stat="stamina" /> ·{" "}
      {formatDuration(eta)}
    </Button>
  );

  return (
    <Sheet
      title={title}
      onClose={() => select(null)}
      testId="tile-sheet"
      actions={
        <span className="badge mono" data-testid="tile-coords">
          {sel.x},{sel.y}
        </span>
      }
    >
      {revenge ? <div className="revenge-briefing" data-testid="revenge-briefing"><div><strong>+{revenge.damagePct}% {t("world.damage")}</strong><p>{t(now + eta >= revenge.expiresAt ? "world.revengeTooLate" : "world.revengeOnce")}</p></div><Timer finishesAt={revenge.expiresAt} /></div> : null}
      <div className="hint" style={{ marginBottom: 8 }}>
        {ring ? pick(ring.name) : ""} · {t("world.distance", { n: Math.round(distance) })} ·{" "}
        {t("world.eta", { time: formatDuration(eta) })}
        {tile && !tile.live ? ` · ${t("world.fogged")}` : ""}
      </div>
      {!tile ? <p className="dim">{t("world.emptyTile")}</p> : null}
      {tile ? <TargetIntel tile={tile} /> : null}
      {tile && ["pack", "elite", "vault", "hive", "boss", "enclave"].includes(tile.kind) && !own ? (
        <label
          className="battle-follow-choice"
          title={pick({
            de: "Bei Feindkontakt, solange die Karte geöffnet ist.",
            en: "On contact while the map stays open.",
          })}
        >
          <input type="checkbox" checked={watchBattle} onChange={(e) => setWatchBattle(e.target.checked)} />
          {pick({ de: "Kampf automatisch öffnen", en: "Auto-open battle" })}
        </label>
      ) : null}

      {tile?.kind === "node" ? (
        <>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="badge">
              {t(
                `res.${{ depot: "RAT", dump: "POL", mine: "ALY", well: "ENC" }[tile.node ?? "depot"] ?? "RAT"}`,
              )}
            </span>
            {tile.remaining !== null ? (
              <span className="badge">{t("world.remaining", { n: formatCompact(tile.remaining) })}</span>
            ) : null}
            {tile.gatherers > 0 ? <span className="badge warn">{t("world.occupied")}</span> : null}
          </div>
          {squadPicker}
          {tile && !own && ["node", "ruin"].includes(tile.kind) ? (
            <p className="hint" data-testid="march-action-cost">
              {t("world.stamina.cost", { n: 0 })}
            </p>
          ) : null}
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <Button
              variant="primary"
              disabled={busy || marchSlotsFull || !chosen?.ready || tile.gatherers > 0}
              onClick={() => void act(() => march({ x: sel.x, y: sel.y, action: "gather" }))}
              data-testid="march-gather"
            >
              <FunctionalIcon name="gather" /> {t("world.gather")} · {formatDuration(eta)}
            </Button>
          </div>
        </>
      ) : null}

      {tile && (tile.kind === "pack" || tile.kind === "elite" || tile.kind === "vault") ? (
        <>
          {squadPicker}
          {tile && !own && ["node", "ruin"].includes(tile.kind) ? (
            <p className="hint" data-testid="march-action-cost">
              {t("world.stamina.cost", { n: 0 })}
            </p>
          ) : null}
          <div className="target-deploy">{attackButton(tile.kind, "march-attack")}</div>
        </>
      ) : null}

      {tile?.kind === "hive" ? (
        <>
          <div className="progress" style={{ margin: "6px 0" }}>
            <div
              className="progress-fill"
              style={{ width: `${tile.hive?.hpPct ?? 100}%`, background: "var(--danger, #ef4444)" }}
            />
          </div>
          <p className="hint">{t("world.desc.hive", { hp: Math.round(tile.hive?.hpPct ?? 100) })}</p>
          {squadPicker}
          {tile && !own && ["node", "ruin"].includes(tile.kind) ? (
            <p className="hint" data-testid="march-action-cost">
              {t("world.stamina.cost", { n: 0 })}
            </p>
          ) : null}
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {attackButton("hive", "march-attack")}
            <Button
              disabled={busy}
              onClick={() => void act(() => scout(sel.x, sel.y))}
              data-testid="march-scout"
            >
              <FunctionalIcon name="eye" /> {t("world.scout")} /{" "}
              {t("world.stamina.cost", { n: STAMINA_COST["scout"] ?? 0 })}
            </Button>
          </div>
        </>
      ) : null}

      {tile?.kind === "boss" ? (
        <>
          <p className="hint">
            {t("world.desc.boss", { hp: Math.round(tile.boss?.hpPct ?? 100) })}{" "}
            {tile.boss ? <Timer finishesAt={tile.boss.windowUntil} /> : null}
          </p>
          {squadPicker}
          {tile && !own && ["node", "ruin"].includes(tile.kind) ? (
            <p className="hint" data-testid="march-action-cost">
              {t("world.stamina.cost", { n: 0 })}
            </p>
          ) : null}
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {attackButton("boss", "march-attack")}
          </div>
        </>
      ) : null}

      {tile?.kind === "ruin" ? (
        <>
          <p className="hint">{t("world.desc.ruin")}</p>
          {squadPicker}
          {tile && !own && ["node", "ruin"].includes(tile.kind) ? (
            <p className="hint" data-testid="march-action-cost">
              {t("world.stamina.cost", { n: 0 })}
            </p>
          ) : null}
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <Button
              variant="primary"
              disabled={busy || marchSlotsFull || !chosen?.ready}
              onClick={() =>
                void act(() => march({ x: sel.x, y: sel.y, action: "explore", choice: "force" }))
              }
              data-testid="ruin-force"
            >
              <FunctionalIcon name="build" /> {t("world.ruin.force")}
            </Button>
            <Button
              variant="primary"
              disabled={busy || marchSlotsFull || !chosen?.ready}
              onClick={() => void act(() => march({ x: sel.x, y: sel.y, action: "explore", choice: "hack" }))}
              data-testid="ruin-hack"
            >
              <FunctionalIcon name="save" /> {t("world.ruin.hack")}
            </Button>
          </div>
        </>
      ) : null}

      {tile?.kind === "camp" ? (
        <div data-testid="camp-sheet">
          <p className="hint">{t("world.desc.camp")}</p>
          {camp ? (
            <>
              <div className="hint" style={{ marginTop: 6 }}>
                {t("world.camp.tradesLeft", { n: camp.tradesLeft })}
              </div>
              {camp.trades.map((tr) => (
                <div key={tr.id} className="row list-row">
                  <span className="grow">
                    {Object.entries(tr.give)
                      .map(([k, v]) => `${formatCompact(v)} ${t(`res.${k}`)}`)
                      .join(" + ")}{" "}
                    <FunctionalIcon name="arrowRight" /> <strong>{tr.get.SCR} $</strong>
                  </span>
                  <Button
                    style={{ minHeight: 32 }}
                    disabled={
                      busy ||
                      camp.tradesLeft <= 0 ||
                      Object.entries(tr.give).some(([k, v]) => (balances?.[k as "RAT"] ?? 0) < v)
                    }
                    onClick={() =>
                      void act(() =>
                        run(
                          () =>
                            worldApi
                              .campTrade(sel.x, sel.y, tr.id)
                              .then((c) => useWorld.setState({ camp: c })),
                          t("world.toast.traded"),
                        ),
                      )
                    }
                    data-testid={`trade-${tr.id}`}
                  >
                    {t("world.camp.trade")}
                  </Button>
                </div>
              ))}
              {camp.quests.map((q) => (
                <div key={q.id} className="row list-row" data-testid={`quest-${q.id}`}>
                  <span className="grow">
                    {pick(q.name)}
                    <div className="hint">
                      {q.progress} / {q.target}
                    </div>
                  </span>
                  {q.claimed ? (
                    <span className="badge ok">
                      <FunctionalIcon name="check" />
                    </span>
                  ) : q.claimable ? (
                    <Button
                      variant="gold"
                      style={{ minHeight: 32 }}
                      disabled={busy}
                      onClick={() =>
                        void act(() =>
                          run(
                            () =>
                              worldApi
                                .campClaim(sel.x, sel.y, q.id)
                                .then((c) => useWorld.setState({ camp: c })),
                            t("world.toast.claimed"),
                          ),
                        )
                      }
                      data-testid={`claim-${q.id}`}
                    >
                      {t("world.camp.claim")}
                    </Button>
                  ) : null}
                </div>
              ))}
            </>
          ) : (
            <p className="dim">{t("common.loading")}</p>
          )}
        </div>
      ) : null}

      {tile?.kind === "citadel" ? <p className="hint">{t("world.desc.citadel")}</p> : null}

      {tile?.kind === "enclave" && tile.owner && !own ? (
        <>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <strong data-testid="tile-owner">{tile.owner.name}</strong>
            <span className="badge">HQ {tile.owner.hq}</span>
            {tile.live ? (
              <span className="badge">
                <StatIcon stat="power" /> {formatCompact(tile.owner.power)}
              </span>
            ) : null}
            {tile.owner.bot ? <span className="badge">{t("world.npc")}</span> : null}
            {tile.owner.alliance ? <span className={`badge relation-${tile.owner.alliance.relation}`} data-testid="tile-owner-alliance">[{tile.owner.alliance.tag}] {t(`alliance.relation.${tile.owner.alliance.relation}`)}</span> : null}
            {tile.owner.shielded ? (
              <span className="badge ok">
                <GameIcon id="emote.shield" /> {t("world.shielded")}
              </span>
            ) : null}
            {tile.owner.burning ? (
              <span className="badge warn">
                <GameIcon id="emote.fire" /> {t("world.burning")}
              </span>
            ) : null}
          </div>
          {squadPicker}
          {tile && !own && ["node", "ruin"].includes(tile.kind) ? (
            <p className="hint" data-testid="march-action-cost">
              {t("world.stamina.cost", { n: 0 })}
            </p>
          ) : null}
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {attackButton("enclave", "march-attack")}
            <Button
              disabled={busy}
              onClick={() => void act(() => scout(sel.x, sel.y))}
              data-testid="march-scout"
            >
              <FunctionalIcon name="eye" /> {t("world.scout")} /{" "}
              {t("world.stamina.cost", { n: STAMINA_COST["scout"] ?? 0 })} · 2 min
            </Button>
            <Button
              disabled={busy}
              onClick={() =>
                void act(() =>
                  socialApi.assist(tile.owner!.id).then(() => toast.success(t("world.assisted"))),
                )
              }
              data-testid="tile-assist"
            >
              <FunctionalIcon name="people" /> {t("world.assist")}
            </Button>
          </div>
        </>
      ) : null}

      {own ? <OwnActions /> : null}

      <details className="field-details">
        <summary>{pick({ de: "Kartenaktionen", en: "Map actions" })}</summary>
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <Button
            style={{ minHeight: 32 }}
            onClick={() => setJump({ x: sel.x, y: sel.y })}
            data-testid="tile-centre"
          >
            <FunctionalIcon name="location" /> {t("world.centre")}
          </Button>
          <Button
            style={{ minHeight: 32 }}
            disabled={busy}
            onClick={() => void bookmark()}
            data-testid="tile-bookmark"
          >
            <FunctionalIcon name="bookmark" /> {t("world.bookmark")}
          </Button>
          <Button style={{ minHeight: 32 }} onClick={() => void share()} data-testid="tile-share">
            ⎘ {t("world.share")}
          </Button>
        </div>
      </details>
    </Sheet>
  );
}

/** Shield, teleport and extinguish on the own enclave (R44, store scope). */
export function OwnActions() {
  const t = useT();
  const me = useWorld((s) => s.me);
  const run = useWorld((s) => s.run);
  const setJump = useWorld((s) => s.setJump);
  const [busy, setBusy] = useState(false);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  if (!me) return null;
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };
  const hours = Object.keys(SHIELDS.credits).map(Number);
  const attackOut = me.marches.some((m) => m.action === "attack" && m.state === "marching");
  return (
    <div data-testid="own-actions">
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {me.shieldUntil || me.newbieUntil ? (
          <span className="badge ok" data-testid="own-shield">
            <GameIcon id="emote.shield" />{" "}
            <Timer finishesAt={Math.max(me.shieldUntil ?? 0, me.newbieUntil ?? 0)} />
          </span>
        ) : (
          <span className="badge warn">{t("world.noShield")}</span>
        )}
        <span className="badge">
          <GameIcon id="bld.bastion_wall" label={t("world.wall")} /> {me.wall.pct}%
          {me.wall.repairsAt ? (
            <>
              {" "}
              · <Timer finishesAt={me.wall.repairsAt} />
            </>
          ) : null}
        </span>
        {me.burningUntil ? (
          <span className="badge warn">
            <GameIcon id="emote.fire" /> <Timer finishesAt={me.burningUntil} />
          </span>
        ) : null}
        <span className="badge">
          <GameIcon id="ui.medal.gold" /> {me.prestige}
        </span>
      </div>
      <div className="section" style={{ marginTop: 10 }}>
        <div className="hint">{attackOut ? t("world.shield.blocked") : t("world.shield.buy")}</div>
        <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 6 }}>
          {hours.map((h) => (
            <Button
              key={h}
              variant="gold"
              style={{ minHeight: 32 }}
              disabled={busy || attackOut || credits < (me.shieldPrices[String(h)] ?? Infinity)}
              onClick={() =>
                void act(() => run(() => worldApi.shield(h, "credits"), t("world.toast.shielded")))
              }
              data-testid={`shield-${h}`}
            >
              <GameIcon id="itm.shield_8h" /> {h < 24 ? `${h} h` : `${h / 24} d`} ·{" "}
              {me.shieldPrices[String(h)] ?? "?"} ₵
            </Button>
          ))}
        </div>
      </div>
      <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 10 }}>
        <Button
          variant="gold"
          style={{ minHeight: 32 }}
          disabled={busy || credits < me.teleport.random}
          onClick={() =>
            void act(() =>
              run(() => worldApi.teleport("random").then((p) => setJump(p)), t("world.toast.teleported")),
            )
          }
          data-testid="teleport-random"
        >
          ⤳ {t("world.teleport.random")} · {me.teleport.random} ₵
        </Button>
        {me.burningUntil ? (
          <Button
            variant="gold"
            style={{ minHeight: 32 }}
            disabled={busy || credits < me.extinguishCredits}
            onClick={() => void act(() => run(() => worldApi.extinguish(), t("world.toast.extinguished")))}
            data-testid="extinguish"
          >
            <FunctionalIcon name="extinguish" /> {t("world.extinguish")} · {me.extinguishCredits} ₵
          </Button>
        ) : null}
      </div>
      {me.teleport.newbieFree ? (
        <div className="hint" style={{ marginTop: 6 }}>
          {t("world.teleport.newbieHint")}
        </div>
      ) : null}
    </div>
  );
}
